import { PrimaryTeamMemberCard } from './PrimaryTeamMemberCard'
import { TeamMemberCard } from './TeamMemberCard'
import { AddAgentCard } from './AddAgentCard'
import { Flex, LoadingSpinner, Heading, IconButton, Button, Spacer, Box } from '@homebotapp/hb-react-component-catalog'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import {
  selectCustomerIsRealEstateAgent,
  selectCustomerIsInsuranceAgent,
  selectCustomerIsLoanOfficer
} from '../../../store/selectors/customerProfile'
import { selectClientTeamMemberships } from '../../../store/selectors/clientTeamMemberships'
import { useCanViewHbnTeamPage } from '../../../hooks/useCanViewHbnTeamPage'
import { RedirectToHomeDigest } from '../Navigation'
import { fetchProfile } from '../../../actions/customerProfile'
import { fetchClientData, fetchClientHomes, setCurrentClient } from '../../../actions/client'
import { useParams } from 'react-router-dom'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { defineMessages, useIntl } from 'react-intl'
import { IconAngleLeft } from '../Icon'
import { AppLayout } from '../../../App/Layout/AppLayout'
import { fetchBuyerInfoData } from '../../../actions/buyerInfo'

const MSG = defineMessages({
  yourTeam: {
    id: 'Homeowners.overlay.team.title',
    defaultMessage: 'Your Team'
  },
  back: {
    id: 'Button.Back',
    defaultMessage: 'Back'
  }
})

export const TeamPanel = () => {
  const intl = useIntl()
  const history = useHistory()
  const match = useRouteMatch()
  const [isLoanOfficerLoading, setIsLoanOfficerLoading] = useState(true)
  const clientTeamMemberships = useSelector(selectClientTeamMemberships)
  const isREA = useSelector(selectCustomerIsRealEstateAgent)
  const isInsuranceAgent = useSelector(selectCustomerIsInsuranceAgent)
  const isLoanOfficer = useSelector(selectCustomerIsLoanOfficer)
  const canViewHbnTeamPage = useCanViewHbnTeamPage()

  const teamMemberships = clientTeamMemberships?.data ?? []
  const dispatch = useDispatch()
  const isLoading = isLoanOfficerLoading

  // Check if team already has members of specific types
  const hasRealEstateAgent = teamMemberships.some(member => member.customerProfile.type === 'RealEstateAgent')
  const hasInsuranceAgent = teamMemberships.some(member => member.customerProfile.type === 'InsuranceAgent')
  const hasLoanOfficer = teamMemberships.some(member => member.customerProfile.type === 'LoanOfficer')
  const handleLoanOfficerLoadingChange = useCallback((loading: boolean) => {
    setIsLoanOfficerLoading(loading)
  }, [])

  const handleBackClick = useCallback(() => {
    history.goBack()
  }, [history])

  const clientId = useParams<{ clientId: string }>()?.clientId
  const customerProfileId = useParams<{ customerProfileId: string }>()?.customerProfileId

  // must populate redux store so that nav links work correctly if page is refreshed
  // ideally these calls would be hoisted higher up in the component tree this gets it working for now
  useEffect(() => {
    dispatch(setCurrentClient(clientId))
    dispatch(fetchClientData(clientId, customerProfileId))
    dispatch(fetchClientHomes(clientId))
    dispatch(fetchBuyerInfoData(clientId))
  }, [dispatch, clientId, customerProfileId])

  useEffect(() => {
    if (customerProfileId) {
      dispatch(fetchProfile(customerProfileId))
    }
  }, [dispatch, customerProfileId])

  // Redirect if user can't view the team page
  if (canViewHbnTeamPage === false) {
    return <RedirectToHomeDigest />
  }

  return (
    <AppLayout loading={isLoading} enableAgentDropdown={false} match={match}>
      <Flex mb={16} pt={18} pb={[20, 20, 8]} flexDir='column' alignItems='center' px={[4, 6, 8]}>
        <Flex mb={10} justifyContent='space-between' alignItems='center' width={['100%', '100%', '375px']}>
          <Button aria-label='Go back' leftIcon={<IconAngleLeft />} onClick={handleBackClick} variant='ghost' size='sm'>
            {intl.formatMessage(MSG.back)}
          </Button>
          <Heading mb={0} ml={4} size='lg'>
            {intl.formatMessage(MSG.yourTeam)}
          </Heading>
          <Spacer />
        </Flex>

        <Box mb={10}>
          <PrimaryTeamMemberCard onLoadingChange={handleLoanOfficerLoadingChange} />
        </Box>
        {isLoading ? (
          <Flex justify='center' align='center' minH='200px'>
            <LoadingSpinner />
          </Flex>
        ) : (
          <>
            {teamMemberships.map((membership, index) => (
              <Box key={membership.id || index} mb={10}>
                <TeamMemberCard key={membership.id || index} membership={membership} clientId={clientId} />
              </Box>
            ))}
            {!isREA && !hasRealEstateAgent && (
              <Box mb={3}>
                <AddAgentCard clientId={clientId} customerType='RealEstateAgent' />
              </Box>
            )}
            {!isLoanOfficer && !hasLoanOfficer && (
              <Box mb={3}>
                <AddAgentCard clientId={clientId} customerType='LoanOfficer' />
              </Box>
            )}
            {!isInsuranceAgent && !hasInsuranceAgent && (
              <Box mb={3}>
                <AddAgentCard clientId={clientId} customerType='InsuranceAgent' />
              </Box>
            )}
          </>
        )}
      </Flex>
    </AppLayout>
  )
}
